import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  ChevronLeft,
  Loader2,
  AlertCircle,
  Send,
  Sparkles
} from 'lucide-react';
import { useToast } from '../components/ToastProvider';
import { SkeletonDetail } from '../components/Skeleton';
import {
  RequestFormStepContent,
  RequestFormStepIndicator,
  REQUEST_FORM_STEPS,
} from '../features/requests/RequestFormSteps';
import {
  REQUEST_STEP_FIELDS,
  missingRequiredRequestFields,
  requestFieldStep,
  validateRequestFormData,
  validateRequestFormField,
} from '../features/requests/requestFormValidation';
import { apiRequest } from '../services/apiClient';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';

function createEmptyRequestFormData() {
  return {
    // Vendor Info
    vendorName: '',
    vendorWebsite: '',
    vendorContactName: '',
    vendorEmail: '',
    vendorAddress: '',
    // Lightspark Details
    lightsparkEntity: '',
    serviceOwner: '',
    scope: '',
    serviceDescription: '',
    // Security Assessment
    ndaStatus: '',
    confidentialInfoShared: '',
    aiMlUsage: '',
    aiTrainingUse: '',
    dataTypes: '',
    dataAccessLevel: '',
    dataRetention: '',
    systemAccess: '',
    systemAccessDescription: '',
    customerInteraction: '',
    criticalToOperations: '',
    ipProduction: '',
    aiCodeOrIpGeneration: '',
    relatedParty: '',
    riskRating: '',
    // Contract Details
    agreementType: '',
    agreementTypeOther: '',
    vendorForm: '',
    estimatedFees: '',
    feeFrequency: '',
    requiresCeoApproval: '',
    ceoApprovalEvidence: '',
    startDate: '',
    endDate: '',
    expenseReimbursement: '',
    notes: ''
  };
}

export default function RequestForm() {
  const navigate = useNavigate();
  // Edit mode (/tprm/:id/edit): same form, hydrated from the request, saving
  // via PUT. The backend only allows requester edits in draft /
  // changes_requested — this is what closes the "changes requested →
  // fix → resubmit" loop.
  const { id } = useParams();
  const isEditing = Boolean(id);
  const { showToast } = useToast();

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(isEditing);
  const [requestStatus, setRequestStatus] = useState(null);
  const [error, setError] = useState(null);
  // AI prefill from pasted notes — fills only empty fields; the requester
  // still walks every step to confirm.
  const [prefillOpen, setPrefillOpen] = useState(false);
  const [prefillNotes, setPrefillNotes] = useState('');
  const [prefillBusy, setPrefillBusy] = useState(false);
  const [prefilledFields, setPrefilledFields] = useState(null);

  // Inline field-level validation — same touched/error pattern as the
  // contract form: errors appear on blur and clear as the field is fixed.
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [formData, setFormData] = useState(createEmptyRequestFormData);
  // Snapshot of the last saved/loaded form state, for the unsaved-changes
  // guard below.
  const [baseline, setBaseline] = useState(() =>
    JSON.stringify(createEmptyRequestFormData())
  );

  const isDirty = !loading && JSON.stringify(formData) !== baseline;
  const bypassUnsavedGuard = useUnsavedChangesGuard(isDirty);

  // Hydrate from the existing request in edit mode. Dates arrive as stored
  // strings; date inputs need bare YYYY-MM-DD.
  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    apiRequest(`/requests/${id}`)
      .then((req) => {
        if (cancelled) return;
        if (!['draft', 'changes_requested'].includes(req.status)) {
          showToast('This request can no longer be edited', 'error');
          navigate(`/tprm/${id}`, { replace: true });
          return;
        }
        setRequestStatus(req.status);
        const next = createEmptyRequestFormData();
        for (const key of Object.keys(next)) {
          const value = req[key];
          if (value === null || value === undefined) continue;
          next[key] = typeof value === 'string' && ['startDate', 'endDate'].includes(key)
            ? value.slice(0, 10)
            : value;
        }
        setFormData(next);
        setBaseline(JSON.stringify(next));
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' ? (value === '' ? '' : Number(value)) : value;
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    if (touched[name]) {
      setErrors((prev) => ({ ...prev, [name]: validateRequestFormField(name, newValue) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateRequestFormField(name, value) }));
  };

  const runPrefill = async () => {
    if (prefillBusy || !prefillNotes.trim()) return;
    setPrefillBusy(true);
    try {
      const body = await apiRequest('/requests/prefill', { json: { notes: prefillNotes } });
      const suggestions = body.suggestions || {};
      const found = Object.keys(suggestions).length;

      // Apply to empty fields only — never clobber something the requester
      // already typed. Computed against current state (not inside the state
      // updater) so the toast can describe exactly what happened.
      const next = { ...formData };
      let applied = 0;
      for (const [key, value] of Object.entries(suggestions)) {
        if (key in next && (next[key] === '' || next[key] === null)) {
          next[key] = key === 'estimatedFees' ? Number(String(value).replace(/[$,\s]/g, '')) || '' : value;
          applied += 1;
        }
      }
      setFormData(next);
      // Re-check fields the requester already touched — a prefilled value
      // should clear a lingering "required" error without another blur.
      setErrors((prev) => {
        const updated = { ...prev };
        for (const key of Object.keys(prev)) {
          if (touched[key]) updated[key] = validateRequestFormField(key, next[key]);
        }
        return updated;
      });
      setPrefilledFields(applied);
      setPrefillOpen(false);

      if (found === 0) {
        showToast('No form answers found in those notes — try pasting something with more detail', 'error');
      } else {
        const skipped = found - applied;
        const stillMissing = missingRequiredRequestFields(next);
        const parts = [
          `Found ${found} answer${found === 1 ? '' : 's'}`,
          applied !== found ? `applied ${applied}` : 'all applied',
        ];
        if (skipped > 0) parts.push(`${skipped} skipped (already filled)`);
        let message = `${parts.join(' — ')}. Review each step before submitting.`;
        if (stillMissing.length > 0) {
          message += ` Still needed: ${stillMissing.map((f) => f.label).join(', ')}.`;
        }
        showToast(message, 'success');
      }
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setPrefillBusy(false);
    }
  };

  const handleNext = () => {
    // Validate just the step being left, so fields the requester hasn't
    // reached yet don't light up red.
    const stepFields = REQUEST_STEP_FIELDS[currentStep] || [];
    const stepErrors = stepFields.reduce((acc, key) => {
      const error = validateRequestFormField(key, formData[key]);
      return error ? { ...acc, [key]: error } : acc;
    }, {});
    if (Object.keys(stepErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...stepErrors }));
      setTouched((prev) => ({
        ...prev,
        ...stepFields.reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      }));
      showToast('Please fix the highlighted fields', 'error');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const payload = () => ({
    ...formData,
    estimatedFees: formData.estimatedFees === '' ? null : Number(formData.estimatedFees),
  });

  // Create (POST) or update (PUT), returning the request id either way.
  const persistRequest = async () => {
    if (isEditing) {
      await apiRequest(`/requests/${id}`, { method: 'PUT', json: payload() });
      return id;
    }
    const newRequest = await apiRequest('/requests', { method: 'POST', json: payload() });
    return newRequest.id;
  };

  const handleSaveDraft = async () => {
    const vendorNameError = validateRequestFormField('vendorName', formData.vendorName);
    if (vendorNameError) {
      setErrors((prev) => ({ ...prev, vendorName: vendorNameError }));
      setTouched((prev) => ({ ...prev, vendorName: true }));
      setCurrentStep(1);
      showToast('Please provide at least a vendor name', 'error');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const requestId = await persistRequest();
      showToast(isEditing ? 'Changes saved' : 'Draft saved successfully', 'success');
      bypassUnsavedGuard();
      navigate(`/tprm/${requestId}`);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRequest = async () => {
    const newErrors = validateRequestFormData(formData);
    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      setTouched((prev) => ({
        ...prev,
        ...Object.keys(newErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {}),
      }));
      const missing = missingRequiredRequestFields(formData);
      showToast(
        missing.length > 0
          ? `Missing required field${missing.length === 1 ? '' : 's'}: ${missing.map((f) => f.label).join(', ')}`
          : 'Please fix the highlighted fields before submitting',
        'error'
      );
      // Jump to the step holding the first problem so the inline error is visible.
      setCurrentStep(requestFieldStep(Object.keys(newErrors)[0]));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const requestId = await persistRequest();
      await apiRequest(`/requests/${requestId}/submit`, {
        method: 'POST',
      });

      showToast(
        requestStatus === 'changes_requested'
          ? 'Request resubmitted — both review lanes will take a fresh look'
          : 'Request submitted successfully',
        'success'
      );
      bypassUnsavedGuard();
      navigate(`/tprm/${requestId}`);
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <SkeletonDetail />;

  return (
    <div className="canvas-inner animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div style={{ minWidth: 0 }}>
          <div className="page-eyebrow" style={{ marginBottom: 8 }}>
            <Link
              to="/tprm"
              className="row"
              style={{ gap: 4, color: 'inherit', textDecoration: 'none' }}
            >
              <ArrowLeft className="w-3 h-3" />
              Requests
            </Link>
            <span style={{ margin: '0 6px', opacity: 0.4 }}>·</span>
            <span>TPRM</span>
          </div>
          <h1 className="page-title">{isEditing ? 'Edit TPRM request' : 'New TPRM request'}</h1>
          <div className="page-subtitle">
            {requestStatus === 'changes_requested'
              ? 'Update the request to address the reviewers’ feedback, then resubmit.'
              : isEditing
              ? 'Update your draft, then save or submit it for review.'
              : 'Submit a vendor onboarding request for Legal and Security review.'}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="callout red" style={{ marginBottom: 20 }}>
          <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-red-500)', flexShrink: 0, marginTop: 1 }} />
          <div className="callout-body">
            <strong>Error</strong>
            <div style={{ fontSize: 12, marginTop: 2 }}>{error}</div>
          </div>
        </div>
      )}

      {/* AI prefill from notes */}
      {!prefillOpen && prefilledFields === null && (
        <div style={{ marginBottom: 16 }}>
          <button type="button" className="btn outline xs" onClick={() => setPrefillOpen(true)}>
            <Sparkles className="w-3 h-3" />
            Prefill from notes
          </button>
        </div>
      )}
      {prefillOpen && (
        <div className="card" style={{ padding: '16px 18px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
            Prefill from notes
          </div>
          <div className="muted" style={{ fontSize: 12, marginBottom: 10, lineHeight: 1.5 }}>
            Paste a vendor email, quote, or call notes. Whatever the notes clearly answer gets
            pre-filled — you review every step before submitting.
          </div>
          <textarea
            className="textarea"
            rows={5}
            placeholder="Paste notes here…"
            value={prefillNotes}
            onChange={(e) => setPrefillNotes(e.target.value)}
            disabled={prefillBusy}
            style={{ width: '100%', minHeight: 110, resize: 'vertical', padding: '10px 12px', lineHeight: 1.5, marginBottom: 10 }}
          />
          <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" className="btn ghost sm" disabled={prefillBusy} onClick={() => setPrefillOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn primary sm" disabled={prefillBusy || !prefillNotes.trim()} onClick={runPrefill}>
              {prefillBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Extract answers
            </button>
          </div>
        </div>
      )}

      {/* Step Indicator */}
      <RequestFormStepIndicator
        steps={REQUEST_FORM_STEPS}
        currentStep={currentStep}
        onJump={(stepId) => { if (stepId < currentStep) setCurrentStep(stepId); }}
      />

      {/* Form Content */}
      <div style={{ display: 'grid', gap: 16 }}>
        <RequestFormStepContent
          currentStep={currentStep}
          formData={formData}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
        />
      </div>

      {/* Navigation */}
      <div
        style={{
          marginTop: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingTop: 20,
          borderTop: '0.5px solid var(--border-primary)',
        }}
      >
        <div>
          {currentStep > 1 && (
            <button onClick={handleBack} className="btn outline sm">
              <ChevronLeft className="w-3.5 h-3.5" />
              Back
            </button>
          )}
        </div>

        <div className="row" style={{ gap: 8 }}>
          <button
            onClick={handleSaveDraft}
            disabled={saving || submitting}
            className="btn outline sm"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Saving…' : isEditing ? 'Save changes' : 'Save draft'}
          </button>
          {currentStep < 4 ? (
            <button onClick={handleNext} className="btn primary sm">
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              onClick={handleSubmitRequest}
              disabled={saving || submitting}
              className="btn primary sm"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  {requestStatus === 'changes_requested' ? 'Save & resubmit' : 'Submit request'}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
